import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Card, CardContent } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Plus, Edit, Trash2, Loader2, Upload, ExternalLink, Video } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const extractYouTubeId = (url) => {
  if (!url) return null;
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) ? match[2] : null;
};

export const ProductManager = () => {
  const { getAuthHeader } = useAuth();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image: '',
    link: '',
    categoryId: '',
    youtubeUrl: '',
    seoTitle: '',
    seoDescription: '',
    seoKeywords: ''
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [productsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/products`),
        axios.get(`${API}/categories`)
      ]);
      setProducts(productsRes.data.products);
      setCategories(categoriesRes.data.categories);
    } catch (error) {
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formDataFile = new FormData();
    formDataFile.append('file', file);

    try {
      const response = await axios.post(
        `${API}/upload`,
        formDataFile,
        {
          headers: {
            ...getAuthHeader(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      setFormData({ ...formData, image: `${BACKEND_URL}${response.data.url}` });
      toast.success('Immagine caricata con successo!');
    } catch (error) {
      toast.error('Errore nel caricamento immagine');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingProduct) {
        await axios.put(
          `${API}/products/${editingProduct.id}`,
          formData,
          { headers: getAuthHeader() }
        );
        toast.success('Prodotto aggiornato con successo!');
      } else {
        await axios.post(
          `${API}/products`,
          formData,
          { headers: getAuthHeader() }
        );
        toast.success('Prodotto creato con successo!');
      }

      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Errore nel salvataggio del prodotto');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo prodotto?')) {
      return;
    }

    try {
      await axios.delete(
        `${API}/products/${productId}`,
        { headers: getAuthHeader() }
      );
      toast.success('Prodotto eliminato con successo!');
      fetchData();
    } catch (error) {
      toast.error('Errore eliminazione prodotto');
    }
  };

  const openEditDialog = (product) => {
    setEditingProduct(product);
    setFormData({
      title: product.title,
      description: product.description || '',
      image: product.image,
      link: product.link,
      categoryId: product.categoryId || '',
      youtubeUrl: product.youtubeUrl || '',
      seoTitle: product.seoTitle || '',
      seoDescription: product.seoDescription || '',
      seoKeywords: product.seoKeywords || ''
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingProduct(null);
    setFormData({
      title: '',
      description: '',
      image: '',
      link: '',
      categoryId: '',
      youtubeUrl: '',
      seoTitle: '',
      seoDescription: '',
      seoKeywords: ''
    });
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    resetForm();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Gestione Prodotti</h2>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              onClick={() => resetForm()}
              className="bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Aggiungi Prodotto
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingProduct ? 'Modifica Prodotto' : 'Nuovo Prodotto'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Titolo Prodotto *
                </label>
                <Input
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Biocamino da Parete Elite"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Descrizione Prodotto (opzionale)
                </label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={4}
                  placeholder="Descrivi le caratteristiche del prodotto, dimensioni, materiali, etc..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  Se inserisci una descrizione, apparirà un pulsante per espanderla nella card prodotto
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Immagine Prodotto (opzionale se hai un video YouTube)
                </label>
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <Input
                      value={formData.image}
                      onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                      placeholder="URL immagine o carica file"
                    />
                    <label className="cursor-pointer">
                      <Button
                        type="button"
                        variant="outline"
                        disabled={uploading}
                        asChild
                      >
                        <span>
                          {uploading ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Upload className="w-4 h-4" />
                          )}
                        </span>
                      </Button>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleFileUpload}
                      />
                    </label>
                  </div>
                  {formData.image && (
                    <img
                      src={formData.image}
                      alt="Preview"
                      className="w-full h-48 object-cover rounded-lg"
                    />
                  )}
                  <p className="text-xs text-gray-500">
                    Puoi caricare un'immagine oppure usare solo un video YouTube
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Categoria
                </label>
                <select
                  value={formData.categoryId}
                  onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}
                  className="w-full h-10 px-3 py-2 text-sm bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-600 focus:border-transparent"
                >
                  <option value="">Nessuna categoria</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Link Prodotto (dugez.com) *
                </label>
                <Input
                  required
                  type="url"
                  value={formData.link}
                  onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                  placeholder="https://dugez.com/prodotto-1"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <Video className="w-4 h-4 inline mr-1" />
                  Link Video YouTube (opzionale se hai un'immagine)
                </label>
                <Input
                  type="url"
                  value={formData.youtubeUrl}
                  onChange={(e) => setFormData({ ...formData, youtubeUrl: e.target.value })}
                  placeholder="https://www.youtube.com/watch?v=..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  Puoi usare un video YouTube oppure solo un'immagine
                </p>
                {formData.youtubeUrl && extractYouTubeId(formData.youtubeUrl) && (
                  <div className="mt-3">
                    <p className="text-sm text-gray-600 mb-2">Anteprima video:</p>
                    <div className="relative w-full bg-black rounded-lg overflow-hidden" style={{paddingTop: '56.25%'}}>
                      <iframe
                        className="absolute inset-0 w-full h-full"
                        src={`https://www.youtube.com/embed/${extractYouTubeId(formData.youtubeUrl)}?rel=0`}
                        title="YouTube video preview"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                        loading="lazy"
                      ></iframe>
                    </div>
                  </div>
                )}
              </div>

              {/* SEO Section */}
              <div className="border-t pt-4 mt-4">
                <h4 className="font-semibold text-gray-900 mb-3">SEO - Ottimizzazione Google</h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      SEO Titolo (60-70 caratteri)
                    </label>
                    <Input
                      value={formData.seoTitle}
                      onChange={(e) => setFormData({ ...formData, seoTitle: e.target.value })}
                      placeholder="Biocamino da Parete Elite - Design Italiano"
                      maxLength={70}
                    />
                    <p className="text-xs text-gray-500 mt-1">{formData.seoTitle.length}/70 caratteri</p>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      SEO Descrizione (150-160 caratteri)
                    </label>
                    <Textarea
                      value={formData.seoDescription}
                      onChange={(e) => setFormData({ ...formData, seoDescription: e.target.value })}
                      rows={3}
                      placeholder="Scopri il biocamino da parete Elite: design elegante, installazione facile..."
                      maxLength={160}
                    />
                    <p className="text-xs text-gray-500 mt-1">{formData.seoDescription.length}/160 caratteri</p>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      SEO Keywords (separate da virgola)
                    </label>
                    <Input
                      value={formData.seoKeywords}
                      onChange={(e) => setFormData({ ...formData, seoKeywords: e.target.value })}
                      placeholder="biocamino parete, camino bioetanolo, design italiano"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleDialogClose}
                >
                  Annulla
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  className="bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Salvataggio...
                    </>
                  ) : (
                    editingProduct ? 'Aggiorna' : 'Crea'
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {products.map((product) => {
          const category = categories.find(c => c.id === product.categoryId);
          const hasVideo = !!product.youtubeUrl;
          const hasImage = !!product.image;
          
          return (
            <Card key={product.id} className="overflow-hidden">
              <div className="relative aspect-[4/3] bg-gray-100">
                {hasImage ? (
                  <img
                    src={product.image}
                    alt={product.title}
                    className="w-full h-full object-cover"
                  />
                ) : hasVideo ? (
                  <div className="flex items-center justify-center h-full">
                    <Video className="w-16 h-16 text-gray-400" />
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-400">
                    Nessun media
                  </div>
                )}
                {hasVideo && (
                  <div className="absolute top-2 right-2 bg-red-600 text-white p-2 rounded-full">
                    <Video className="w-4 h-4" />
                  </div>
                )}
                {category && (
                  <div className="absolute top-2 left-2 bg-amber-600 text-white px-3 py-1 rounded-full text-xs font-semibold">
                    {category.name}
                  </div>
                )}
              </div>
              <CardContent className="p-4">
                <h3 className="font-bold text-lg mb-2">{product.title}</h3>
                <a
                  href={product.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-amber-700 hover:text-amber-800 flex items-center gap-1 mb-4"
                >
                  <ExternalLink className="w-3 h-3" />
                  Vedi su dugez.com
                </a>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openEditDialog(product)}
                    className="flex-1"
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Modifica
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(product.id)}
                    className="flex-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Elimina
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {products.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-gray-500">Nessun prodotto ancora. Inizia aggiungendone uno!</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
